import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories, navLinks, siteInfo } from "@/mocks/site";

export default function Navbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  const isHome = pathname === "/";
  const transparent = isHome && !scrolled && !mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-2.5 group shrink-0"
          aria-label={`${siteInfo.name} 首页`}
        >
          <span
            className={`w-10 h-10 rounded-md flex items-center justify-center text-lg transition-colors ${
              transparent
                ? "bg-primary-500 text-background-50"
                : "bg-primary-500 text-background-50"
            }`}
          >
            <i className="ri-film-line" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg md:text-xl font-bold tracking-wide ${
                transparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              {siteInfo.name}
            </span>
            <span
              className={`text-[11px] mt-1 tracking-widest ${
                transparent ? "text-background-100" : "text-foreground-500"
              }`}
            >
              花絮 · 路透 · 侧拍
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
          {navLinks.map((item) => {
            const active =
              item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  transparent
                    ? "text-background-50 hover:bg-background-50/15"
                    : active
                    ? "text-primary-700 bg-primary-100"
                    : "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            );
          })}

          <div
            className="relative"
            onMouseEnter={() => setCatOpen(true)}
            onMouseLeave={() => setCatOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 cursor-pointer ${
                transparent
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
              }`}
              aria-expanded={catOpen}
            >
              分类浏览
              <i
                className={`ri-arrow-down-s-line transition-transform ${
                  catOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full pt-2 w-64">
                <div className="bg-background-50 border border-background-200 rounded-lg p-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/collections?cat=${cat.slug}`}
                      className="flex items-start gap-3 px-3 py-2.5 rounded-md hover:bg-background-100 transition-colors"
                    >
                      <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center shrink-0">
                        <i className={cat.icon} aria-hidden="true" />
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-foreground-900">
                          {cat.name}
                        </span>
                        <span className="text-xs text-foreground-500 mt-0.5">
                          {cat.subtitle}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/collections"
            className={`px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              transparent
                ? "bg-background-50 text-foreground-950 hover:bg-background-100"
                : "bg-primary-500 text-background-50 hover:bg-primary-600"
            }`}
          >
            进入合集大厅
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className={`lg:hidden w-10 h-10 rounded-md flex items-center justify-center text-xl cursor-pointer transition-colors ${
            transparent
              ? "text-background-50 hover:bg-background-50/15"
              : "text-foreground-900 hover:bg-background-100"
          }`}
          aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
          aria-expanded={mobileOpen}
        >
          <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"} aria-hidden="true" />
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="px-4 py-4 flex flex-col gap-1" aria-label="移动端导航">
            {navLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="px-4 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-3 pt-3 border-t border-background-200">
              <p className="px-4 text-xs tracking-widest text-foreground-500 mb-2">
                分类浏览
              </p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/collections?cat=${cat.slug}`}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-background-100 hover:bg-secondary-100 transition-colors"
                  >
                    <i className={`${cat.icon} text-secondary-700`} aria-hidden="true" />
                    <span className="text-sm text-foreground-800">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/collections"
              className="mt-4 px-4 py-3 rounded-md text-sm font-medium text-center bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
            >
              进入合集大厅
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}